/**
 * =============================================================================
 * Componente: AffiliateMobileHeader
 * Versão: 1.0.0 (Redesign Premium)
 *
 * Descrição:
 *   Header sticky do mobile com avatar do afiliado, saudação e sino de
 *   notificações. Sem botão de menu — a navegação vive na bottom-nav.
 * =============================================================================
 */
import { UserPlus } from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { AffiliateNotificationBell } from '@/components/affiliate/AffiliateNotificationBell';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { SmartAvatar } from '@/components/ui/smart-avatar';

/** Pega a primeira inicial do nome para o avatar. */
function getInitial(name?: string | null): string {
  if (!name) return 'A';
  const trimmed = name.trim();
  return trimmed.charAt(0).toUpperCase() || 'A';
}

/** Saudação adequada ao horário local. */
function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

interface AffiliateMobileHeaderProps {
  /** Callback opcional para abrir o modal de "Nova Indicação". */
  onNewReferral?: () => void;
}

export function AffiliateMobileHeader({ onNewReferral }: AffiliateMobileHeaderProps) {
  const { affiliate } = useAffiliateAuth();
  const firstName = affiliate?.full_name?.split(' ')[0] ?? 'Afiliado';
  const initial = getInitial(affiliate?.full_name);

  return (
    <header
      className={cn(
        'sticky top-0 z-30 flex h-14 items-center gap-3 px-4 md:hidden',
        'bg-[hsl(var(--aff-bg))]/85 backdrop-blur-xl',
        'border-b border-[hsl(var(--aff-border))]/60',
        'aff-safe-top',
      )}
    >
      <SmartAvatar
        src={affiliate?.avatar_url}
        name={affiliate?.full_name}
        size="sm"
        fallback={initial}
        fallbackClassName="text-[hsl(var(--aff-primary-foreground))]"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-[hsl(var(--aff-muted-foreground))]">
          {greeting()}
        </p>
        <p className="truncate text-sm font-semibold text-[hsl(var(--aff-foreground))]">
          {firstName}
        </p>
      </div>
      {onNewReferral && (
        <Button
          type="button"
          size="sm"
          onClick={onNewReferral}
          aria-label="Nova Indicação"
          className="h-9 w-9 rounded-full bg-[hsl(var(--aff-primary))] p-0 text-[hsl(var(--aff-primary-foreground))] hover:bg-[hsl(var(--aff-primary)/0.9)]"
        >
          <UserPlus className="h-4 w-4" />
        </Button>
      )}
      <AffiliateNotificationBell />
    </header>
  );
}
